import React, { useState, useEffect } from 'react';
import { Percentage } from '@browsing/icons';

import { SEC_SUB_SUCCESS_PLACEMENT } from '@client/config/constants';
import HappyFace from '@client/components/shared/faces/HappyFace';
import SadFace from '@client/components/shared/faces/SadFace';
import NeutralFace from '@client/components/shared/faces/NeutralFace';
import { track } from '@client/helpers/tracking';

export interface MessageProps {
    reject: () => void;
    subscribe: () => void;
    confirmEmail: () => void;
    close: () => void;
    rejected: boolean;
    accepted: boolean;
    error: boolean;
    pending: boolean;
    hasOrders: boolean;
    isDoi: boolean;
    isCucaHelpShown: boolean;
    placement?: string;
}

export interface Step {
    id: string;
    title: string;
    text: string;
    iconComponent: React.Component | React.FunctionComponentElement<SVGElement>;
    onAccept: () => void;
    onReject?: () => void;
    yesMessage?: string | null;
    noMessage?: string;
    placeholder?: boolean;
}

const STEPS = {
    INITIAL: 'initial',
    REJECTED: 'rejected',
    SUCCESS: 'success',
    ERROR: 'error',
};

const getError = ({ isCucaHelpShown, subscribe }: MessageProps): Step => ({
    id: STEPS.ERROR,
    title: 'errorHeader',
    text: isCucaHelpShown ? 'errorTextCuca' : 'errorText',
    yesMessage: isCucaHelpShown ? null : 'errorButton',
    iconComponent: <SadFace />,
    onAccept: subscribe,
});

const getSuccess = ({ isDoi, confirmEmail }: MessageProps): Step => ({
    id: STEPS.SUCCESS,
    title: 'successHeader',
    text: isDoi ? 'successTextDoi' : 'successText',
    yesMessage: isDoi && confirmEmail ? 'successButtonDoi' : null,
    onAccept: isDoi && confirmEmail ? confirmEmail : null,
    placeholder: !isDoi || !confirmEmail,
    iconComponent: <HappyFace />,
});

const getRejected = ({ placement, close, subscribe }: MessageProps): Step => ({
    id: STEPS.REJECTED,
    title: 'rejectionHeader',
    text: 'rejectionText',
    yesMessage: 'rejectionButton',
    iconComponent: <NeutralFace />,
    onAccept: subscribe,
    onReject: placement !== SEC_SUB_SUCCESS_PLACEMENT ? close : null,
});

const getInitial = ({ hasOrders, subscribe, reject }: MessageProps): Step => ({
    id: STEPS.INITIAL,
    title: hasOrders ? 'messageHeaderBuyers' : 'messageHeader',
    text: 'messageText',
    iconComponent: <Percentage />,
    onAccept: subscribe,
    onReject: reject,
});

export const useStep = (props: MessageProps) => {
    const { error, rejected, accepted, isCucaHelpShown } = props;
    const [step, setStep] = useState<Step>(getInitial(props));

    useEffect(() => {
        let updatedStep = getInitial(props);
        if (error) {
            updatedStep = getError(props);
        } else if (accepted) {
            updatedStep = getSuccess(props);
        } else if (rejected) {
            updatedStep = getRejected(props);
        }
        track(`secondary_subscription_${updatedStep.id}_view`);
        setStep(updatedStep);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, rejected, accepted, isCucaHelpShown]);

    return {
        step,
    };
};
